import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type TableSize = 'sm' | 'md';

type TableProps = {
  $size: TableSize;
  $fullWidth?: boolean;
  $striped?: boolean;
  $bordered?: boolean;
  $headerColor?: string;
  $headerBackgroundColor?: string;
  $borderColor?: string;
};

type RowProps = {
  $clickable?: boolean;
};

type CellAlignProps = {
  $align?: 'left' | 'center' | 'right';
  $width?: string;
};

const sizeStyles: Record<TableSize, ReturnType<typeof css>> = {
  sm: css`
    font-size: 12px;

    th,
    td {
      padding: 8px 10px;
    }
  `,
  md: css`
    font-size: 14px;

    th,
    td {
      padding: 12px 16px;
    }
  `,
};

export const TableWrap = styled.div<{ $fullWidth?: boolean }>`
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  overflow-x: auto;
`;

export const StyledTable = styled('table', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TableProps>`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Mangueira', sans-serif;
  color: ${theme.colors.primaryText};
  background: #fff;

  ${({ $size }) => sizeStyles[$size]}

  thead th {
    color: ${({ $headerColor }) => $headerColor || theme.colors.gray};
    background: ${({ $headerBackgroundColor }) =>
      $headerBackgroundColor || '#FAFAFA'};
    font-weight: 600;
    white-space: nowrap;
  }

  ${({ $bordered, $borderColor }) =>
    $bordered &&
    css`
      border: 1px solid ${$borderColor || '#E8E8E8'};

      th,
      td {
        border-bottom: 1px solid ${$borderColor || '#E8E8E8'};
      }
    `}

  tbody tr:last-of-type td {
    border-bottom: none;
  }
`;

export const StyledHead = styled.thead``;

export const StyledBody = styled.tbody<{ $striped?: boolean }>`
  ${({ $striped }) =>
    $striped &&
    css`
      tr:nth-of-type(even) {
        background: #FAFAFA;
      }
    `}
`;

export const StyledRow = styled('tr', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<RowProps>`
  transition: background 0.15s ease;

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;

      &:hover {
        background: #F5F8FF;
      }
    `}
`;

export const StyledHeaderCell = styled('th', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<CellAlignProps & { $sortable?: boolean }>`
  width: ${({ $width }) => $width || 'auto'};
  text-align: ${({ $align }) => $align || 'left'};

  ${({ $sortable }) =>
    $sortable &&
    css`
      cursor: pointer;
      user-select: none;

      &:hover {
        color: ${theme.colors.blue};
      }
    `}
`;

export const SortWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: auto;
  max-width: 100%;
`;

export const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.colors.gray};
  line-height: 0;

  svg {
    width: 10px;
    height: 14px;
  }
`;

export const StyledCell = styled('td', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<CellAlignProps>`
  width: ${({ $width }) => $width || 'auto'};
  text-align: ${({ $align }) => $align || 'left'};
  vertical-align: middle;
`;

export const EmptyRow = styled.tr``;

export const EmptyCell = styled.td`
  padding: 24px 16px !important;
  text-align: center;
  color: ${theme.colors.gray};
`;
